/** Telegram: vínculo pessoal (Minha conta) e administração (Configurações › Integrações). */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { confirmar, toast } from "./feedback";
import { Card, ErrorBox, Loading, useAct } from "./kit";

interface TgStatus { configured: boolean; botUsername: string | null; mode: string; linked: { username: string | null; createdAt: string; lastSeenAt: string | null } | null; mfaEnabled: boolean }
type TgPrefs = { telegramResumo: boolean; telegramFatal: boolean; telegramAtribuicao: boolean; telegramIntimacao: boolean; telegramTitulos: boolean };

export function TelegramCard(): JSX.Element {
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ["telegram-status"], queryFn: () => api.get<TgStatus>("/telegram/status") });
  const { data: prefs } = useQuery({ queryKey: ["notif-prefs"], queryFn: () => api.get<TgPrefs & Record<string, unknown>>("/notificacoes/preferencias") });
  const [code, setCode] = useState<{ code: string; deepLink: string | null; expiresAt: string } | null>(null);
  const gen = useAct(() => api.post<{ code: string; deepLink: string | null; expiresAt: string }>("/telegram/codigo"), [], (r) => setCode(r as { code: string; deepLink: string | null; expiresAt: string }), false);
  const unlink = useAct(() => api.del("/telegram/vinculo"), [["telegram-status"]], () => setCode(null), "Telegram desvinculado");
  const setPref = async (patch: Partial<TgPrefs>) => {
    if (!prefs) return;
    const { email: _e, ...rest } = { ...prefs, ...patch } as Record<string, unknown>;
    try { await api.put("/notificacoes/preferencias", rest); await qc.invalidateQueries({ queryKey: ["notif-prefs"] }); } catch (e) { toast.error((e as Error).message); }
  };
  if (error) return <Card title="Telegram"><ErrorBox error={error} /></Card>;
  if (!data) return <Card title="Telegram"><Loading /></Card>;
  if (!data.configured) return <Card title="Telegram"><div className="muted small">O bot do Telegram ainda não foi configurado no servidor.</div></Card>;
  return (
    <div className="card compact">
      <h2>Telegram</h2>
      {data.linked ? (
        <div className="stack" style={{ gap: 8 }}>
          <div className="row"><span className="tag tag-green">Vinculado</span>
            <span className="muted small">{data.linked.username ? `@${data.linked.username} · ` : ""}desde {fmtDateTime(data.linked.createdAt)}{data.linked.lastSeenAt ? ` · último uso ${fmtDateTime(data.linked.lastSeenAt)}` : ""}</span>
            <span className="spacer" />
            <button className="btn small danger" onClick={() => void confirmar("Desligar o Telegram da sua conta?").then((ok) => ok && unlink.mutate())}>Desvincular</button>
          </div>
          <div className="muted small">No Telegram, use <b>/menu</b>. Financeiro só para o gerente financeiro, com o código do autenticador{data.mfaEnabled ? "" : <> — <b>ative a verificação em duas etapas acima para usar</b></>}.</div>
          {prefs && (
            <>
              <b className="small" style={{ marginTop: 6 }}>Receber no Telegram</b>
              <label className="row nowrap"><input type="checkbox" checked={prefs.telegramResumo} onChange={(e) => void setPref({ telegramResumo: e.target.checked })} /> Resumo do dia às 7h (só contagens)</label>
              <label className="row nowrap"><input type="checkbox" checked={prefs.telegramFatal} onChange={(e) => void setPref({ telegramFatal: e.target.checked })} /> Prazo fatal no dia útil anterior</label>
              <label className="row nowrap"><input type="checkbox" checked={prefs.telegramAtribuicao} onChange={(e) => void setPref({ telegramAtribuicao: e.target.checked })} /> Atividade nova, para revisar ou devolvida</label>
              <label className="row nowrap"><input type="checkbox" checked={prefs.telegramIntimacao} onChange={(e) => void setPref({ telegramIntimacao: e.target.checked })} /> Intimação nova atribuída a mim</label>
              <label className="row nowrap" style={{ marginTop: 6 }}><input type="checkbox" checked={prefs.telegramTitulos} onChange={(e) => void setPref({ telegramTitulos: e.target.checked })} /> Mostrar títulos completos das atividades <span className="muted small">(podem conter nomes de clientes — por padrão só número, tipo e processo)</span></label>
            </>
          )}
        </div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          <div className="muted small">Ligue o bot <b>@{data.botUsername}</b> à sua conta para consultar tarefas, prazos, agenda, processos e (gerente financeiro) o financeiro, e receber avisos.</div>
          {!code ? <div><button className="btn primary" disabled={gen.isPending} onClick={() => gen.mutate()}>Gerar código de vínculo</button></div> : (
            <div className="stack" style={{ gap: 6 }}>
              <div>Código: <code style={{ fontSize: 18, letterSpacing: 2 }}>{code.code}</code> <span className="muted small">(vale até {fmtDateTime(code.expiresAt)})</span></div>
              {code.deepLink && <div><a className="btn" href={code.deepLink} target="_blank" rel="noreferrer">Abrir no Telegram</a></div>}
              <div className="muted small">Ou, no Telegram, abra @{data.botUsername} e envie <code>/start {code.code}</code>. Depois, clique em “Atualizar”.</div>
              <div><button className="btn small" onClick={() => void qc.invalidateQueries({ queryKey: ["telegram-status"] })}>Atualizar</button></div>
            </div>
          )}
          <ErrorBox error={gen.error} />
        </div>
      )}
    </div>
  );
}

interface TgAdmin {
  configured: boolean; botUsername: string | null; mode: string;
  links: Array<{ id: string; name: string; username: string | null; createdAt: string; lastSeenAt: string | null; locked: boolean }>;
  groups: Array<{ id: string; title: string | null; alerts: string[]; createdAt: string }>;
  groupAlerts: string[];
}
const ALERT_LABEL: Record<string, string> = { captura: "Falha na captura", prazos: "Prazos do dia (7h)", intimacoes: "Intimações pendentes (7h)" };

export function TelegramAdmin(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["telegram-admin"], queryFn: () => api.get<TgAdmin>("/telegram/admin") });
  const [groupCode, setGroupCode] = useState<{ command: string; expiresAt: string } | null>(null);
  const unlink = useAct((id: string) => api.del(`/telegram/admin/vinculos/${id}`), [["telegram-admin"]], undefined, "Vínculo removido");
  const gen = useAct(() => api.post<{ command: string; expiresAt: string }>("/telegram/admin/grupos/codigo"), [], (r) => setGroupCode(r as { command: string; expiresAt: string }), false);
  const upd = useAct((v: { id: string; alerts: string[] }) => api.patch(`/telegram/admin/grupos/${v.id}`, { alerts: v.alerts }), [["telegram-admin"]]);
  const del = useAct((id: string) => api.del(`/telegram/admin/grupos/${id}`), [["telegram-admin"]], undefined, "Grupo removido");
  if (error) return <Card title="Telegram"><ErrorBox error={error} /></Card>;
  if (!data) return <Card title="Telegram"><Loading /></Card>;
  return (
    <Card title="Telegram">
      {!data.configured ? (
        <div className="muted small">Bot não configurado. No servidor: crie o bot com o @BotFather e coloque <code>TELEGRAM_BOT_TOKEN</code> no .env (em produção também <code>TELEGRAM_MODE=webhook</code>, <code>TELEGRAM_WEBHOOK_URL</code> e <code>TELEGRAM_WEBHOOK_SECRET</code>). Reinicie a API e o worker.</div>
      ) : (
        <div className="stack gap-16">
          <div className="muted small">Bot <b>@{data.botUsername}</b> · modo {data.mode === "webhook" ? "webhook (produção)" : "consulta periódica (QA)"}. Cada pessoa vincula em Minha conta › Telegram.</div>
          <div>
            <b>Pessoas vinculadas ({data.links.length})</b>
            <table className="table compact"><tbody>
              {data.links.map((l) => (
                <tr key={l.id}>
                  <td>{l.name}{l.locked && <span className="tag tag-red" style={{ marginLeft: 6 }}>financeiro bloqueado</span>}</td>
                  <td className="muted small">{l.username ? `@${l.username}` : "—"}</td>
                  <td className="muted small">desde {fmtDateTime(l.createdAt)}{l.lastSeenAt ? ` · último uso ${fmtDateTime(l.lastSeenAt)}` : ""}</td>
                  <td className="right"><button className="btn small danger" onClick={() => void confirmar(`Desvincular o Telegram de ${l.name}?`).then((ok) => ok && unlink.mutate(l.id))}>Desvincular</button></td>
                </tr>
              ))}
              {!data.links.length && <tr><td className="muted small">Ninguém vinculado ainda.</td></tr>}
            </tbody></table>
          </div>
          <div>
            <div className="row between"><b>Grupos (só alertas)</b><button className="btn small" onClick={() => gen.mutate()}>Gerar código de grupo</button></div>
            {groupCode && <div className="muted small" style={{ margin: "6px 0" }}>Adicione @{data.botUsername} ao grupo e envie lá: <code>{groupCode.command}</code> (vale até {fmtDateTime(groupCode.expiresAt)}).</div>}
            <table className="table compact"><tbody>
              {data.groups.map((g) => (
                <tr key={g.id}>
                  <td>{g.title ?? "Grupo"}</td>
                  <td>{data.groupAlerts.map((a) => (
                    <label key={a} className="check" style={{ marginRight: 12 }}>
                      <input type="checkbox" checked={g.alerts.includes(a)} onChange={(e) => upd.mutate({ id: g.id, alerts: e.target.checked ? [...g.alerts, a] : g.alerts.filter((x) => x !== a) })} /> {ALERT_LABEL[a] ?? a}
                    </label>
                  ))}</td>
                  <td className="right"><button className="btn small danger" onClick={() => void confirmar("Remover este grupo? Ele deixa de receber alertas.").then((ok) => ok && del.mutate(g.id))}>Remover</button></td>
                </tr>
              ))}
              {!data.groups.length && <tr><td className="muted small">Nenhum grupo vinculado.</td></tr>}
            </tbody></table>
          </div>
        </div>
      )}
    </Card>
  );
}
